import { X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LocationMapPicker } from './location-map-picker';
import type { RadiusFormValue } from './radius-form';
import type { BusinessLocationRead, Location } from '@/lib/types';

interface MapLocationDialogProps {
  open: boolean;
  businessId: number;
  onClose: () => void;
  /** When opened from a radius row's Edit, jump straight into editing that radius. */
  editRadius?: BusinessLocationRead | null;
  /** Current draft working sets (owned by the parent). The dialog is a thin
   * translator: picker events become parent draft mutations — nothing is
   * persisted here. Saving happens on the page's "Save Changes". */
  catalogSelected: Location[];
  /** Criteria ids a campaign positively targets — locked from removal here (the
   * backend reconcile rejects dropping them, #4816). The picker greys their
   * Remove affordance; the diff below also refuses to stage their removal. */
  lockedCriteriaIds?: Set<number>;
  radii: RadiusFormValue[];
  onAddCatalog: (loc: Location) => void;
  onRemoveCatalog: (criteriaId: number) => void;
  onAddRadius: (r: RadiusFormValue) => void;
  onRemoveRadius: (lat: number, lng: number) => void;
  onEditRadius: (lat: number, lng: number, next: RadiusFormValue) => void;
}

const radiusKey = (r: RadiusFormValue) => `rad:${r.lat}:${r.lng}`;

/** Full-screen (90vw x 90vh) map-driven "Add location" dialog (#5325). Edits are
 * staged into the parent's local draft; the footer "Done" only closes. Everything
 * persists together when the user clicks the page-level "Save Changes". */
export function MapLocationDialog({
  open,
  businessId,
  onClose,
  editRadius = null,
  catalogSelected,
  lockedCriteriaIds,
  radii,
  onAddCatalog,
  onRemoveCatalog,
  onAddRadius,
  onRemoveRadius,
  onEditRadius,
}: MapLocationDialogProps) {
  // Diff the catalog working set against the parent draft: stage adds + removes.
  const handleSelectedChange = (next: Location[]) => {
    const added = next.filter((n) => !catalogSelected.some((s) => s.criteria_id === n.criteria_id));
    // Never stage removal of a campaign-locked location: the backend reconcile
    // would 409 and lose every other staged change (#4816). The picker greys the
    // affordance, but guard here too so no removal path can slip a locked id out.
    const removed = catalogSelected.filter(
      (s) => !next.some((n) => n.criteria_id === s.criteria_id) && !lockedCriteriaIds?.has(s.criteria_id),
    );
    added.forEach(onAddCatalog);
    removed.forEach((loc) => onRemoveCatalog(loc.criteria_id));
  };

  // Diff the radius working set: stage adds, removes, and in-place edits.
  const handleRadiiChange = (next: RadiusFormValue[]) => {
    const added = next.filter((n) => !radii.some((x) => radiusKey(x) === radiusKey(n)));
    const removed = radii.filter((x) => !next.some((n) => radiusKey(n) === radiusKey(x)));
    const changed = next.filter((n) =>
      radii.some(
        (x) => radiusKey(x) === radiusKey(n) && (x.radiusKm !== n.radiusKm || x.formattedAddress !== n.formattedAddress),
      ),
    );
    added.forEach(onAddRadius);
    removed.forEach((r) => onRemoveRadius(r.lat, r.lng));
    changed.forEach((r) => onEditRadius(r.lat, r.lng, r));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        // [&>button]:hidden suppresses the default absolutely-positioned close button so we can
        // render our own ✕ in the flex header, vertically centered against the title.
        className="flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[90vw] flex-col gap-0 overflow-hidden p-0 [&>button]:hidden"
        aria-describedby={undefined}
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 border-b border-gray-200 px-5 py-4">
          <DialogTitle>{editRadius ? 'Edit radius' : 'Add location'}</DialogTitle>
          <DialogClose className="rounded-sm text-gray-400 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="min-h-0 flex-1">
          <LocationMapPicker
            businessId={businessId}
            selected={catalogSelected}
            lockedCriteriaIds={lockedCriteriaIds}
            onChange={handleSelectedChange}
            radii={radii}
            onRadiiChange={handleRadiiChange}
            initialRadiusEdit={
              editRadius && editRadius.latitude != null && editRadius.longitude != null && editRadius.radius_km != null
                ? {
                    lat: editRadius.latitude,
                    lng: editRadius.longitude,
                    radiusKm: editRadius.radius_km,
                    countryCode: editRadius.country_code,
                    formattedAddress: editRadius.radius_address ?? `${editRadius.latitude}, ${editRadius.longitude}`,
                    unit: editRadius.radius_unit ?? 'km',
                  }
                : null
            }
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <Button
            className="bg-[#290BD9] text-white hover:bg-[#2309C5]"
            onClick={onClose}
            data-testid="map-location-done"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MapLocationDialog;
